(function () {
  'use strict';

  if (window.UnifiedContext) {
    return;
  }

  const VERSION = '1.1.0';

  const SESSION_KEY =
    'ut_session_id';

  const PAGE_SEQUENCE_KEY =
    'ut_page_sequence';

  const initializedAt =
    new Date().toISOString();

  const pageLoadedAtMs =
    typeof performance !== 'undefined' &&
    typeof performance.timeOrigin === 'number'
      ? performance.timeOrigin
      : Date.now();

  let maxScrollDepth = 0;

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function normalizePath(path) {
    let value =
      String(path || '/')
        .trim();

    if (!value) {
      return '/';
    }

    if (
      value.length > 1 &&
      value.endsWith('/')
    ) {
      value =
        value.slice(0, -1);
    }

    return value;
  }

  /*
   * ------------------------------------------------------------
   * SESSION STORAGE
   * ------------------------------------------------------------
   */

  function storageGet(key) {
    try {
      return window.sessionStorage
        .getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(
    key,
    value
  ) {
    try {
      window.sessionStorage
        .setItem(
          key,
          String(value)
        );

      return true;
    } catch (error) {
      return false;
    }
  }

  /*
   * ------------------------------------------------------------
   * SESSION ID
   * ------------------------------------------------------------
   */

  function generateSessionId() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID ===
        'function'
    ) {
      return (
        'ut_' +
        window.crypto.randomUUID()
      );
    }

    const randomPart =
      Math.random()
        .toString(36)
        .slice(2);

    const timePart =
      Date.now()
        .toString(36);

    return (
      'ut_' +
      timePart +
      '_' +
      randomPart
    );
  }

  function getOrCreateSessionId() {
    const existing =
      storageGet(
        SESSION_KEY
      );

    if (existing) {
      return existing;
    }

    const sessionId =
      generateSessionId();

    storageSet(
      SESSION_KEY,
      sessionId
    );

    return sessionId;
  }

  /*
   * ------------------------------------------------------------
   * PAGE SEQUENCE
   * ------------------------------------------------------------
   */

  function createPageSequence() {
    const existing =
      Number(
        storageGet(
          PAGE_SEQUENCE_KEY
        )
      );

    const previous =
      Number.isFinite(
        existing
      )
        ? existing
        : 0;

    const next =
      previous + 1;

    storageSet(
      PAGE_SEQUENCE_KEY,
      next
    );

    return next;
  }

  const sessionId =
    getOrCreateSessionId();

  const pageSequence =
    createPageSequence();

  /*
   * ------------------------------------------------------------
   * PAGE INFORMATION
   * ------------------------------------------------------------
   */

  function getPagePath() {
    return normalizePath(
      window.location &&
      window.location.pathname
        ? window.location.pathname
        : '/'
    );
  }

  function getPageLocation() {
    return (
      window.location &&
      window.location.href
        ? window.location.href
        : ''
    );
  }

  function getPageTitle() {
    return (
      document.title ||
      ''
    ).trim();
  }

  function getPageLanguage() {
    const language =
      document.documentElement &&
      document.documentElement.lang
        ? document.documentElement.lang
        : '';

    return String(
      language || ''
    )
      .trim()
      .toLowerCase();
  }

  /*
   * ------------------------------------------------------------
   * REFERRER
   *
   * Only hostname is retained.
   * Full referrer URLs are deliberately
   * excluded from Unified Context.
   * ------------------------------------------------------------
   */

  function getReferrerDomain() {
    if (!document.referrer) {
      return '';
    }

    try {
      return new URL(
        document.referrer
      ).hostname
        .trim()
        .toLowerCase();
    } catch (error) {
      return '';
    }
  }

  /*
   * ------------------------------------------------------------
   * NAVIGATION TYPE
   * ------------------------------------------------------------
   */

  function getNavigationType() {
    try {
      if (
        window.performance &&
        typeof window.performance
          .getEntriesByType ===
          'function'
      ) {
        const entries =
          window.performance
            .getEntriesByType(
              'navigation'
            );

        if (
          entries &&
          entries.length &&
          entries[0].type
        ) {
          return String(
            entries[0].type
          )
            .trim()
            .toLowerCase();
        }
      }
    } catch (error) {
      // Legacy fallback below.
    }

    try {
      if (
        window.performance &&
        window.performance.navigation
      ) {
        const type =
          window.performance
            .navigation.type;

        const map = {
          0: 'navigate',
          1: 'reload',
          2: 'back_forward',
          255: 'reserved'
        };

        return (
          map[type] ||
          'unknown'
        );
      }
    } catch (error) {
      // Best effort.
    }

    return 'unknown';
  }

  /*
   * ------------------------------------------------------------
   * TIME
   * ------------------------------------------------------------
   */

  function getTimeSincePageLoadMs() {
    return Math.max(
      0,
      Date.now() -
        pageLoadedAtMs
    );
  }

  function getSecondsSincePageLoad() {
    return Number(
      (
        getTimeSincePageLoadMs() /
        1000
      ).toFixed(3)
    );
  }

  /*
   * ------------------------------------------------------------
   * SCROLL DEPTH
   * ------------------------------------------------------------
   */

  function calculateScrollDepth() {
    const documentElement =
      document.documentElement;

    const body =
      document.body;

    const scrollTop =
      window.pageYOffset ||
      documentElement.scrollTop ||
      (
        body
          ? body.scrollTop
          : 0
      ) ||
      0;

    const viewportHeight =
      window.innerHeight ||
      documentElement.clientHeight ||
      0;

    const documentHeight =
      Math.max(
        body
          ? body.scrollHeight
          : 0,

        body
          ? body.offsetHeight
          : 0,

        documentElement
          .clientHeight,

        documentElement
          .scrollHeight,

        documentElement
          .offsetHeight
      );

    if (
      !documentHeight ||
      documentHeight <= 0
    ) {
      return 0;
    }

    const depth =
      (
        (
          scrollTop +
          viewportHeight
        ) /
        documentHeight
      ) * 100;

    return Math.min(
      100,
      Math.max(
        0,
        Number(
          depth.toFixed(1)
        )
      )
    );
  }

  function refreshScrollDepth() {
    const current =
      calculateScrollDepth();

    if (
      current >
      maxScrollDepth
    ) {
      maxScrollDepth =
        current;
    }

    return maxScrollDepth;
  }

  /*
   * ------------------------------------------------------------
   * EVENT CONFIG
   * ------------------------------------------------------------
   */

  function getEventConfig(
    eventName
  ) {
    const config =
      getConfig();

    if (
      !config.events ||
      !config.events[
        eventName
      ]
    ) {
      return {};
    }

    return config.events[
      eventName
    ];
  }

  /*
   * ------------------------------------------------------------
   * SERVICE RESOLUTION
   * ------------------------------------------------------------
   */

  function resolveServiceFromPages() {
    const config =
      getConfig();

    const pages =
      config.pages || {};

    const currentPath =
      getPagePath();

    const pageNames =
      Object.keys(
        pages
      );

    for (
      let index = 0;
      index <
      pageNames.length;
      index += 1
    ) {
      const pageName =
        pageNames[index];

      const definition =
        pages[
          pageName
        ] || {};

      const paths =
        Array.isArray(
          definition.paths
        )
          ? definition.paths
          : [];

      const matches =
        paths.some(
          function (path) {
            return (
              normalizePath(
                path
              ) ===
              currentPath
            );
          }
        );

      if (matches) {
        return pageName;
      }
    }

    /*
     * Generic Shopify fallbacks.
     */

    if (
      currentPath.startsWith(
        '/products/'
      ) ||
      currentPath.startsWith(
        '/collections/'
      ) ||
      currentPath ===
        '/cart' ||
      currentPath.startsWith(
        '/search'
      )
    ) {
      return 'shop';
    }

    if (
      currentPath.startsWith(
        '/blogs/'
      )
    ) {
      return 'journal';
    }

    return 'general';
  }

  function resolveService(
    eventName,
    payload
  ) {
    /*
     * Explicit listener/service context
     * has highest priority.
     */

    if (
      isPlainObject(
        payload
      ) &&
      typeof payload.service ===
        'string' &&
      payload.service.trim()
    ) {
      return payload.service
        .trim()
        .toLowerCase();
    }

    const eventConfig =
      getEventConfig(
        eventName
      );

    if (
      typeof eventConfig.service ===
        'string' &&
      eventConfig.service !==
        'dynamic' &&
      eventConfig.service.trim()
    ) {
      return eventConfig.service
        .trim()
        .toLowerCase();
    }

    return resolveServiceFromPages();
  }

  /*
   * ------------------------------------------------------------
   * EVENT CATEGORY
   * ------------------------------------------------------------
   */

  function resolveEventCategory(
    eventName
  ) {
    const eventConfig =
      getEventConfig(
        eventName
      );

    if (
      typeof eventConfig.category ===
        'string' &&
      eventConfig.category.trim()
    ) {
      return eventConfig.category
        .trim()
        .toLowerCase();
    }

    return 'engagement';
  }

  /*
   * ------------------------------------------------------------
   * CONVERSION CANDIDATE
   * ------------------------------------------------------------
   */

  function resolveConversionCandidate(
    eventName
  ) {
    const eventConfig =
      getEventConfig(
        eventName
      );

    return (
      eventConfig.conversion ===
      true
    );
  }

  /*
   * ------------------------------------------------------------
   * CONTEXT CREATION
   * ------------------------------------------------------------
   */

  function getContext(
    eventName,
    payload
  ) {
    refreshScrollDepth();

    return {
      ut_session_id:
        sessionId,

      page_sequence:
        pageSequence,

      page_location:
        getPageLocation(),

      page_path:
        getPagePath(),

      page_title:
        getPageTitle(),

      page_language:
        getPageLanguage(),

      page_loaded_at:
        new Date(
          pageLoadedAtMs
        ).toISOString(),

      time_since_page_load:
        getTimeSincePageLoadMs(),

      seconds_since_page_load:
        getSecondsSincePageLoad(),

      scroll_depth:
        refreshScrollDepth(),

      referrer_domain:
        getReferrerDomain(),

      navigation_type:
        getNavigationType(),

      event_timestamp:
        new Date()
          .toISOString(),

      event_category:
        resolveEventCategory(
          eventName
        ),

      service:
        resolveService(
          eventName,
          payload
        ),

      conversion_candidate:
        resolveConversionCandidate(
          eventName
        )
    };
  }

  /*
   * ------------------------------------------------------------
   * BUS
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        // Legacy fallback below.
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                new Date()
                  .toISOString(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ENRICH PAYLOAD
   * ------------------------------------------------------------
   */

  function enrich(
    eventName,
    payload
  ) {
    const sourcePayload =
      isPlainObject(
        payload
      )
        ? Object.assign(
            {},
            payload
          )
        : {};

    const context =
      getContext(
        eventName,
        sourcePayload
      );

    /*
     * Context is applied last.
     *
     * A listener cannot overwrite:
     * - session ID
     * - timestamps
     * - page context
     * - event category
     * - conversion flag
     *
     * An explicit service is still respected
     * because resolveService() reads it before
     * context is produced.
     */

    const enrichedPayload =
      Object.assign(
        {},
        sourcePayload,
        context
      );

    /*
     * Privacy-oriented diagnostic event:
     * we deliberately DO NOT store the
     * complete pre-validation payload
     * inside UnifiedBus history.
     */

    emit(
      'event_enriched',
      {
        event:
          eventName,

        event_category:
          enrichedPayload
            .event_category,

        service:
          enrichedPayload
            .service,

        page_path:
          enrichedPayload
            .page_path,

        page_sequence:
          enrichedPayload
            .page_sequence,

        scroll_depth:
          enrichedPayload
            .scroll_depth,

        field_names:
          Object.keys(
            enrichedPayload
          )
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Context] Event enriched',
        {
          event:
            eventName,

          service:
            enrichedPayload
              .service,

          category:
            enrichedPayload
              .event_category,

          pagePath:
            enrichedPayload
              .page_path,

          pageSequence:
            enrichedPayload
              .page_sequence
        }
      );
    }

    return {
      success:
        true,

      event:
        eventName,

      payload:
        enrichedPayload,

      context:
        context
    };
  }

  /*
   * ------------------------------------------------------------
   * SCROLL OBSERVER
   * ------------------------------------------------------------
   */

  let scrollTicking =
    false;

  function handleScroll() {
    if (scrollTicking) {
      return;
    }

    scrollTicking =
      true;

    window.requestAnimationFrame(
      function () {
        refreshScrollDepth();

        scrollTicking =
          false;
      }
    );
  }

  window.addEventListener(
    'scroll',
    handleScroll,
    {
      passive: true
    }
  );

  window.addEventListener(
    'resize',
    refreshScrollDepth,
    {
      passive: true
    }
  );

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      refreshScrollDepth,
      {
        once: true
      }
    );
  } else {
    refreshScrollDepth();
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedContext = {
    version:
      VERSION,

    enrich:
      enrich,

    getContext:
      getContext,

    getSessionId:
      function () {
        return sessionId;
      },

    getPageSequence:
      function () {
        return pageSequence;
      },

    getScrollDepth:
      function () {
        return refreshScrollDepth();
      },

    getPagePath:
      getPagePath,

    resolveService:
      resolveService,

    refreshScrollDepth:
      refreshScrollDepth,

    getState:
      function () {
        return {
          version:
            VERSION,

          session_id:
            sessionId,

          page_sequence:
            pageSequence,

          page_path:
            getPagePath(),

          scroll_depth:
            refreshScrollDepth(),

          initialized_at:
            initializedAt
        };
      }
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Context] Context initialized',
      {
        version:
          VERSION,

        sessionId:
          sessionId,

        pageSequence:
          pageSequence,

        pagePath:
          getPagePath(),

        service:
          resolveService(
            '',
            {}
          )
      }
    );
  }
})();